import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import { atom, useAtom, useAtomValue } from "jotai";
import { useCallback } from "react";

const manuallyAddedSourcesAtom = atom<ChatSource[]>([]);
const workflowAgentAddedSourcesAtom = atom<ChatSource[]>([]);
const workflowAgentAddedSourcesUUIDsAtom = atom<string[]>([]);
const scopedSourcesAtom = atom<ChatSource[]>([]);

// Derived atom to compute UUIDs from manually added sources
const manuallyAddedSourcesUUIDsAtom = atom((get) => {
  const sources = get(manuallyAddedSourcesAtom);
  return new Set(sources.map(getSourceUUID).filter(Boolean) as string[]);
});

const scopedSourcesUUIDsAtom = atom((get) => {
  const sources = get(scopedSourcesAtom);
  return new Set(sources.map(getSourceUUID).filter(Boolean) as string[]);
});

export const useManuallyAddedSources = () => {
  const [manuallyAddedSources, setManuallyAddedSources] = useAtom(
    manuallyAddedSourcesAtom,
  );
  const manuallyAddedSourcesUUIDs = useAtomValue(manuallyAddedSourcesUUIDsAtom);

  const addSource = useCallback(
    (source: ChatSource) => {
      const uuid = getSourceUUID(source);
      if (!uuid) {
        return; // Skip if no UUID
      }

      setManuallyAddedSources((prevSources) => {
        const sourceUUIDs = new Set(prevSources.map(getSourceUUID));
        if (sourceUUIDs.has(uuid)) {
          return prevSources; // Skip if source already exists
        }
        return [...prevSources, source];
      });
    },
    [setManuallyAddedSources],
  );

  const removeSource = useCallback(
    (sourceToRemove: ChatSource) => {
      const sourceUUID = getSourceUUID(sourceToRemove);
      if (!sourceUUID) {
        return;
      }

      setManuallyAddedSources((prevSources) =>
        prevSources.filter((s) => {
          const currentUUID = getSourceUUID(s);
          return currentUUID !== sourceUUID;
        }),
      );
    },
    [setManuallyAddedSources],
  );

  const isSourceAdded = useCallback(
    (source: ChatSource) => {
      const uuid = getSourceUUID(source);
      return uuid ? manuallyAddedSourcesUUIDs.has(uuid) : false;
    },
    [manuallyAddedSourcesUUIDs],
  );

  return {
    addSource,
    removeSource,
    isSourceAdded,
    manuallyAddedSources,
    manuallyAddedSourcesUUIDs,
    setManuallyAddedSources,
  };
};

export const useWorkflowAgentAddedSources = () => {
  const [workflowAgentAddedSources, setWorkflowAgentAddedSources] = useAtom(
    workflowAgentAddedSourcesAtom,
  );
  const [workflowAgentAddedSourcesUUIDs, setWorkflowAgentAddedSourcesUUIDs] =
    useAtom(workflowAgentAddedSourcesUUIDsAtom);
  return {
    workflowAgentAddedSources,
    setWorkflowAgentAddedSources,
    workflowAgentAddedSourcesUUIDs,
    setWorkflowAgentAddedSourcesUUIDs,
  };
};

/**
 * This behaves identically to manuallyAddedSources, but is meant to be used
 * for conversations (or messages) that are scoped to a specific set of sources.
 */
export const useScopedSources = () => {
  const [scopedSources, setScopedSources] = useAtom(scopedSourcesAtom);
  const scopedSourcesUUIDs = useAtomValue(scopedSourcesUUIDsAtom);

  const addSource = useCallback(
    (source: ChatSource) => {
      const uuid = getSourceUUID(source);
      if (!uuid) {
        return; // Skip if no UUID
      }

      setScopedSources((prevSources) => {
        const sourceUUIDs = new Set(prevSources.map(getSourceUUID));
        if (sourceUUIDs.has(uuid)) {
          return prevSources; // Skip if source already exists
        }
        return [...prevSources, source];
      });
    },
    [setScopedSources],
  );

  const removeSource = useCallback(
    (sourceToRemove: ChatSource) => {
      const sourceUUID = getSourceUUID(sourceToRemove);
      if (!sourceUUID) {
        return;
      }

      setScopedSources((prevSources) =>
        prevSources.filter((s) => {
          const currentUUID = getSourceUUID(s);
          return currentUUID !== sourceUUID;
        }),
      );
    },
    [setScopedSources],
  );

  const isSourceAdded = useCallback(
    (source: ChatSource) => {
      const uuid = getSourceUUID(source);
      return uuid ? scopedSourcesUUIDs.has(uuid) : false;
    },
    [scopedSourcesUUIDs],
  );

  return {
    scopedSources,
    scopedSourcesUUIDs,
    addScopedSource: addSource,
    removeScopedSource: removeSource,
    isScopedSourceAdded: isSourceAdded,
    setScopedSources,
  };
};
